export const getGridColumnSettingKey = (gridKey, field, property) =>
  `${gridKey}.col.${field}.${property}`;

export const createGridColumnSettingEntry = (
  gridKey,
  field,
  property,
  value,
) => ({
  key: getGridColumnSettingKey(gridKey, field, property),
  value: String(value ?? "null"),
});

export const createGridColumnSettingEntries = (gridKey, columns) =>
  columns.flatMap(({ field, visible, order }) => [
    createGridColumnSettingEntry(gridKey, field, "visible", visible !== false),
    createGridColumnSettingEntry(gridKey, field, "width", null),
    createGridColumnSettingEntry(gridKey, field, "order", order),
  ]);

const normalizeDefaults = (defaultColumns) =>
  defaultColumns.map((column, index) => ({
    ...column,
    visible: column.visible !== false,
    order: Number.isFinite(column.order) ? column.order : index,
  }));

const mergeSavedColumns = (defaultColumns, savedColumns) => {
  const defaults = normalizeDefaults(defaultColumns);
  const savedByField = new Map(
    (Array.isArray(savedColumns) ? savedColumns : [])
      .filter((column) => column?.field)
      .map((column) => [column.field, column]),
  );

  return defaults
    .map((column, defaultIndex) => {
      const saved = savedByField.get(column.field);
      if (!saved) return { ...column, __defaultIndex: defaultIndex };

      const merged = {
        ...column,
        visible:
          typeof saved.visible === "boolean" ? saved.visible : column.visible,
        order: Number.isFinite(Number(saved.order))
          ? Number(saved.order)
          : column.order,
        __defaultIndex: defaultIndex,
      };

      if (Object.prototype.hasOwnProperty.call(saved, "width")) {
        const width = Number(saved.width);
        if (saved.width == null || !Number.isFinite(width) || width < 1) {
          delete merged.width;
        } else {
          merged.width = width;
        }
      }

      return merged;
    })
    .sort(
      (left, right) =>
        left.order - right.order || left.__defaultIndex - right.__defaultIndex,
    )
    .map(({ __defaultIndex, ...column }, index) => ({
      ...column,
      order: index,
    }));
};

const readSavedColumns = (gridKey, defaultColumns, userSettings) => {
  const defaults = normalizeDefaults(defaultColumns);
  const getValue = (field, property) =>
    userSettings.find(
      (setting) =>
        setting.key ===
        getGridColumnSettingKey(gridKey, field, property),
    )?.value;

  const hasSavedSettings = defaults.some((column) =>
    ["visible", "width", "order"].some(
      (property) => getValue(column.field, property) !== undefined,
    ),
  );

  if (!hasSavedSettings) return null;

  return defaults.map((column) => {
    const visible = getValue(column.field, "visible");
    const width = getValue(column.field, "width");
    const order = getValue(column.field, "order");
    const saved = { field: column.field };

    if (visible !== undefined) saved.visible = visible === "true";
    if (width !== undefined && width !== "null") {
      saved.width = Number(width);
    }
    if (order !== undefined) saved.order = Number(order);

    return saved;
  });
};

export const loadGridColumnSettings = ({
  gridKey,
  defaultColumns,
  userSettings = [],
}) => {
  const savedColumns = readSavedColumns(
    gridKey,
    defaultColumns,
    userSettings,
  );
  return mergeSavedColumns(defaultColumns, savedColumns ?? []);
};

export const cloneDefaultGridColumns = (defaultColumns) =>
  mergeSavedColumns(defaultColumns, []);
